/**
 * EditPhotoDialog.jsx — one photo, one cell: framing, rotation and grading.
 *
 * Framing (fill or fit, zoom, position) belongs to the cell. Rotation and the
 * ten adjustments belong to the PHOTO and follow it into every slide that uses
 * it — unless this slide opts out, which shows the original here only.
 *
 * THE STAGE IS THE PIPELINE, NOT A FILTER. While anything is being dragged the
 * stage is a canvas redrawn through pipeline.renderLive() from the preview's
 * ungraded base pixels: the response is immediate and the maths is the export's.
 * Underneath it sits the ordinary preview layer, which is sharper (a ~1600px
 * bake against a 600px live canvas), so it takes back over the moment
 * previews.js has rendered this exact look — hasPreview() is that signal.
 *
 * GRADING IS COMPARISON. Nobody can judge exposure on one frame in a dark sheet,
 * so the reference slide sits flush against the stage at the same height — a
 * quarter stop reads at the seam — the rest of the carousel is one tap away in
 * the strip below, and a press-and-hold on the stage shows the before.
 *
 * Double-clicking (or double-tapping) any slider handle returns it to default.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Dialog } from '@mui/material';
import { useMobile } from '../../hooks/useMobile';
import {
  centerCell, closeEditor, commitPan, getState, nudgeCell, resetCropAt, resetPhotoLook,
  selectImgById, setCellFit, setCellLookMode, setPhotoLook, setScope, setZoomAt, updatePhotoDims,
  useCarousel, wheelAt,
} from '../../hooks/useCarouselStore';
import {
  beginPan, cellFit, cellImgId, cellPlacement, cellRoleName, effectiveCell, formatSize, panTo,
  slideCells, slideGeom, srcRect, zoomSliderMax,
} from '../../lib/carousel/geometry.js';
import {
  ADJ_ENDS, ADJ_GROUPS, ADJ_LABELS, NEUTRAL_ADJ, cellLook, isNeutralAdj, isQuarterTurn, viewPhoto,
} from '../../lib/carousel/look.js';
import { SCOPE_KINDS, normalizeKind } from '../../lib/carousel/scope.js';
import { renderLive } from '../../lib/carousel/pipeline.js';
import {
  getPreview, getPreviewSource, hasPreview, previewKey, previewsVersion, requestPreview,
  subscribePreviews,
} from '../../lib/carousel/previews.js';
import ScopeBox from './ScopeBox';
import SlideStage, { cellLayerStyle } from './SlideStage';
import './editor.css';

/* Longest side of the live canvas. The pipeline is fill-rate bound (~75ns/px
   where there is no GPU), and this surface only has to survive a drag — the
   sharp preview layer is what sits underneath it the rest of the time. */
const LIVE_MAX = 600;
/* The neighbouring slide is cropped to a band: the stage keeps the room. */
const REF_W = { desk: 190, phone: 96 };
const HOLD_MS = 420;      // press-and-hold on the stage = show the before
/* Two taps at the same spot inside this = put it back. Generous on purpose:
   the first tap re-renders the preview, and a finger should not have to beat
   that. Tapping one spot twice cannot mean anything else — a range jumps to
   where you tap, so the second tap would otherwise be a no-op. */
const TAP_MS = 500;
const SLOP = 8;           // a pointer that moves further than this was a drag

const pad2 = (n) => String(n).padStart(2, '0');
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/* One scratch canvas for the whole page: the crop on show, at stage size, ready
   for the pipeline (which scales nothing — it fetches bilinearly and no more). */
let scratch = null;
function scratchAt(w, h) {
  if (!scratch) scratch = document.createElement('canvas');
  if (scratch.width !== w) scratch.width = w;
  if (scratch.height !== h) scratch.height = h;
  return scratch;
}

/* previews.js builds no base for a photo small enough to paint as it arrived, so
   an untouched local file would have no pixels for the stage to grade. Its own
   blob is those pixels. Only ever a blob — a CDN original would taint this
   canvas for every photo after it — and only while the grade is neutral, where
   grading the already-graded preview is the identity. */
const blobImgs = new Map();
function plainSource(im, look, onReady) {
  if (!isNeutralAdj(look.adj)) return null;
  const url = getPreview(im.id, look);
  if (!url || url.slice(0, 5) !== 'blob:') return null;
  let el = blobImgs.get(url);
  if (!el) {
    el = new Image();
    if (onReady) el.addEventListener('load', onReady, { once: true });
    el.src = url;
    blobImgs.set(url, el);
    if (blobImgs.size > 6) blobImgs.delete(blobImgs.keys().next().value);
  }
  return el.complete && el.naturalWidth ? el : null;
}

function liveSize(w, h) {
  if (!w || !h) return { w: 0, h: 0 };
  const k = Math.min(1, LIVE_MAX / Math.max(w, h));
  return { w: Math.max(1, Math.round(w * k)), h: Math.max(1, Math.round(h * k)) };
}

/**
 * Paints the cell's crop into `canvas` through the pipeline. The base pixels are
 * the preview's — already turned, already downscaled — so this only crops and
 * grades. False when there is nothing to paint yet (the preview layer stands).
 */
function paintLive(canvas, im0, look, cell, rc, fit, plain, onReady) {
  if (!canvas || !cell || !rc) return false;
  const src = getPreviewSource(im0.id, look) || plainSource(im0, look, onReady);
  if (!src) return false;
  const sw = src.width || src.naturalWidth || 0;
  const sh = src.height || src.naturalHeight || 0;
  const want = liveSize(rc.w, rc.h);
  if (!sw || !sh || !want.w) return false;
  if (canvas.width !== want.w) canvas.width = want.w;
  if (canvas.height !== want.h) canvas.height = want.h;
  const im = viewPhoto(im0, look);
  const pad = scratchAt(want.w, want.h);
  const g = pad.getContext('2d');
  if (!g) return false;
  g.clearRect(0, 0, want.w, want.h);
  g.imageSmoothingQuality = 'high';
  try {
    if (fit === 'contain') {
      const pl = cellPlacement(im, rc.w, rc.h, cell, 'contain');
      const kx = want.w / rc.w, ky = want.h / rc.h;
      g.drawImage(src, pl.dx * kx, pl.dy * ky, pl.dw * kx, pl.dh * ky);
    } else {
      const r = srcRect(im, rc.w, rc.h, cell);
      const k = sw / im.w;    // base pixels per baked photo pixel
      const cw = clamp(r.sw * k, 1, sw), chh = clamp(r.sh * k, 1, sh);
      g.drawImage(src, clamp(r.sx * k, 0, sw - cw), clamp(r.sy * k, 0, sh - chh), cw, chh, 0, 0, want.w, want.h);
    }
  } catch {
    return false;           // the bases are an LRU: one can go out from under us
  }
  return renderLive(canvas, pad, plain ? NEUTRAL_ADJ : look);
}

/* Which groups are open outlives the sheet, so re-opening a photo picks up where
   the last one left off. A phone starts with the two long groups folded away. */
let openMemory = null;
function initialOpen(isMobile) {
  if (!openMemory) {
    openMemory = { framing: true, rotation: !isMobile, light: true, colour: !isMobile };
  }
  return openMemory;
}

/**
 * Double-click and double-tap, on the same handler, for a control that a drag
 * also owns. A tap only counts when the pointer barely moved: two quick nudges
 * of a slider must not read as "put it back".
 */
function useTapReset(reset) {
  const st = useRef({ x: 0, y: 0, moved: false, last: 0, lx: 0, ly: 0 });
  return {
    onPointerDown: (e) => {
      const s = st.current;
      s.x = e.clientX; s.y = e.clientY; s.moved = false;
    },
    onPointerMove: (e) => {
      const s = st.current;
      if (Math.abs(e.clientX - s.x) > SLOP || Math.abs(e.clientY - s.y) > SLOP) s.moved = true;
    },
    onPointerUp: (e) => {
      const s = st.current;
      if (s.moved) { s.last = 0; return; }
      const now = e.timeStamp || Date.now();
      const near = Math.abs(e.clientX - s.lx) < 24 && Math.abs(e.clientY - s.ly) < 24;
      if (s.last && now - s.last < TAP_MS && near) {
        s.last = 0;
        reset();
        return;
      }
      s.last = now; s.lx = e.clientX; s.ly = e.clientY;
    },
    // the mouse's own gesture, for a double-click that never produced two taps
    onDoubleClick: reset,
  };
}

function NumBox({ value, min, max, step, label, onChange }) {
  const [txt, setTxt] = useState(() => String(value));
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setTxt(String(value));
  }
  const type = (raw) => {
    setTxt(raw);
    const n = parseFloat(raw);
    if (Number.isFinite(n)) onChange(clamp(n, min, max));
  };
  return (
    <input
      type="number"
      className="cc-ed-num mono"
      min={min}
      max={max}
      step={step}
      value={txt}
      aria-label={label + ' value'}
      onChange={(e) => type(e.target.value)}
      onBlur={() => setTxt(String(value))}
    />
  );
}

function SliderRow({ label, value, min, max, step, def, ends, onChange, suffix }) {
  const tap = useTapReset(() => onChange(def));
  const off = value !== def;
  return (
    <div className={'cc-ed-sl' + (off ? ' is-set' : '')}>
      <div className="cc-ed-sltop">
        <span className="lbl">{label}</span>
        {suffix
          ? <span className="mono cc-ed-val">{suffix}</span>
          : <NumBox value={value} min={min} max={max} step={step} label={label} onChange={onChange} />}
      </div>
      <div className="cc-ed-slmid">
        {ends ? <span className="cc-ed-end">{ends[0]}</span> : null}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          aria-label={label}
          title="Double-click to reset"
          onChange={(e) => onChange(parseFloat(e.target.value))}
          {...tap}
        />
        {ends ? <span className="cc-ed-end">{ends[1]}</span> : null}
      </div>
    </div>
  );
}

function Group({ id, title, open, onToggle, reset, children }) {
  return (
    <section className={'cc-ed-group' + (open ? ' is-open' : '')}>
      <div className="cc-ed-ghead">
        <button
          type="button"
          className="cc-ed-gtoggle"
          aria-expanded={open}
          aria-controls={'cc-ed-g-' + id}
          onClick={onToggle}
        >
          <span className="cc-ed-caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
          <span className="lbl">{title}</span>
        </button>
        {reset ? (
          <button type="button" className="btn tiny ghost" disabled={reset.off} onClick={reset.run}>
            {reset.label || 'Reset'}
          </button>
        ) : null}
      </div>
      <div className="cc-ed-gbody" id={'cc-ed-g-' + id} hidden={!open}>{children}</div>
    </section>
  );
}

/**
 * A neighbouring slide, drawn exactly as the carousel draws it. `interactive` is
 * what makes SlideStage paint the graded ~1600px preview instead of the 480px
 * thumbnail — which is the whole point of putting it here. Its data-si/data-ci
 * go nowhere: the delegated handlers live on CarouselView's own host, and this
 * is in a portal.
 */
function RefSlide({ i, slides, panos, design, imgById, previews }) {
  const sl = slides[i];
  if (!sl) return null;
  return (
    <SlideStage
      sl={sl}
      si={i}
      design={design}
      group={sl.pano ? panos[sl.pano.gid] : null}
      imgById={imgById}
      interactive
      previews={previews}
    />
  );
}

export default function EditPhotoDialog() {
  const { isMobile } = useMobile();
  const editor = useCarousel((s) => s.editor);
  const slides = useCarousel((s) => s.slides);
  const panos = useCarousel((s) => s.panos);
  const design = useCarousel((s) => s.design);
  const scope = useCarousel((s) => s.scope);
  const imgById = useCarousel(selectImgById);

  const [stageEl, setStageEl] = useState(null);
  const [liveEl, setLiveEl] = useState(null);
  const [rowEl, setRowEl] = useState(null);
  const [rowBox, setRowBox] = useState({ w: 0, h: 0 });
  const [dragging, setDragging] = useState(false);
  const [before, setBefore] = useState(false);
  const [onLive, setOnLive] = useState(false);
  const [tick, setTick] = useState(0);
  const [nudge, setNudge] = useState(0);
  const [refI, setRefI] = useState(null);
  const [open, setOpen] = useState(() => initialOpen(isMobile));
  const picksRef = useRef(null);
  const dataRef = useRef(null);
  const bumpRef = useRef(null);
  if (!bumpRef.current) bumpRef.current = () => setNudge((n) => n + 1);
  const bump = bumpRef.current;

  const si = editor ? editor.si : -1;
  const ci = editor ? editor.ci : -1;
  const sl = editor ? slides[si] : null;
  const stored = sl ? sl.cells[ci] : null;
  const group = sl && sl.pano ? { [sl.pano.gid]: panos[sl.pano.gid] } : null;
  const imgId = sl ? cellImgId(sl, ci, group, imgById) : null;
  const im = imgId ? imgById(imgId) : null;
  const isPano = !!(sl && sl.pano);
  const look = im ? cellLook(im, isPano ? null : stored) : null;
  const overridden = !isPano && stored && stored.look === 'none';

  // one subscription for every preview question this sheet asks
  const pv = useSyncExternalStore(subscribePreviews, previewsVersion);
  useEffect(() => {
    if (im && look) requestPreview(im, look, updatePhotoDims);
  }, [im, look]);

  const [W, H] = formatSize(design.fmt);
  const g = sl ? slideGeom(sl, design) : null;
  const rects = sl ? slideCells(sl, W, H, g.pad, g.gap) : null;
  const rc = rects ? (rects[ci] || rects[0]) : null;
  const cell = sl && im ? effectiveCell(sl, ci, group, imgById, W, H, design) : null;
  const fit = sl ? cellFit(sl, stored) : 'fill';
  const sig = im && look ? previewKey(im.id, look) : '';
  const cellSig = cell && rc ? [cell.zoom, cell.ox, cell.oy, fit, rc.w, rc.h].join(',') : '';

  dataRef.current = { im, look, cell, rc, fit };

  // the stage is sized here rather than by the flexbox: it has to keep the
  // cell's aspect AND leave room for the reference band next to it
  const refW = refI == null ? 0 : (isMobile ? REF_W.phone : REF_W.desk);
  const stageH = rc && rowBox.w
    ? Math.max(80, Math.min(rowBox.h, (rowBox.w - refW) * rc.h / rc.w))
    : 0;
  // the strip and the hint line below sit under the stage block, not the column
  const underW = stageH ? Math.round(stageH * rc.w / rc.h) + refW : 0;

  useEffect(() => {
    if (!rowEl || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const r = rowEl.getBoundingClientRect();
      setRowBox((b) => (Math.abs(b.w - r.width) < 1 && Math.abs(b.h - r.height) < 1
        ? b : { w: r.width, h: r.height }));
    });
    ro.observe(rowEl);
    return () => ro.disconnect();
  }, [rowEl]);

  // the neighbour to match against: the slide before this one, or the one after
  useEffect(() => {
    if (!editor) return;
    setBefore(false);
    setDragging(false);
    setRefI(si > 0 ? si - 1 : (si + 1 < slides.length ? si + 1 : null));
  }, [editor, si, slides.length]);

  // keep the slide being edited in sight in the strip when the sheet opens.
  // Waits for the width the stage hands down (underW), and moves the strip's own
  // scroll rather than scrollIntoView, which would take the sheet with it.
  useEffect(() => {
    const host = picksRef.current;
    if (!host || !editor || !underW) return undefined;
    const raf = requestAnimationFrame(() => {
      const now = host.querySelector('.cc-ed-pick.is-now');
      if (!now) return;
      const hr = host.getBoundingClientRect(), nr = now.getBoundingClientRect();
      const want = host.scrollLeft + (nr.left - hr.left) - (hr.width - nr.width) / 2;
      host.scrollLeft = Math.max(0, Math.min(want, host.scrollWidth - host.clientWidth));
    });
    return () => cancelAnimationFrame(raf);
  }, [editor, underW]);

  // a drag anywhere in the sheet puts the stage on the live canvas
  useEffect(() => {
    if (!dragging) return undefined;
    const up = () => setDragging(false);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    return () => {
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
    };
  }, [dragging]);

  // drag to reframe, exactly as a slide does — and hold still to see the before
  useEffect(() => {
    const host = stageEl;
    if (!host || !editor) return undefined;
    const onPointerDown = (e) => {
      if (e.button !== 0) return;
      let hold = setTimeout(() => { hold = 0; setBefore(true); }, HOLD_MS);
      const done = () => {
        if (hold) clearTimeout(hold);
        hold = 0;
        setBefore(false);
      };
      const s = getState();
      const slide = s.slides[si];
      const pan = slide
        ? beginPan(slide, ci, { panos: s.panos, imgById: selectImgById(s), design: s.design }, host.getBoundingClientRect().width)
        : null;
      try { host.setPointerCapture(e.pointerId); } catch { /* already released */ }
      host.classList.add('is-drag');
      const x0 = e.clientX, y0 = e.clientY;
      let pos = pan ? { x: pan.x0, y: pan.y0 } : null;
      const mv = (ev) => {
        const dx = ev.clientX - x0, dy = ev.clientY - y0;
        // ANY real move takes the before-view down, not only one that beats the
        // timer: studying the frame for half a second and then nudging it is the
        // normal way to do this, and it used to leave you framing against pixels
        // that would not export for the whole of the drag.
        if (Math.abs(dx) > SLOP || Math.abs(dy) > SLOP) {
          if (hold) { clearTimeout(hold); hold = 0; }
          setBefore(false);
        }
        if (!pan) return;
        pos = panTo(pan, dx, dy);
        commitPan(si, pan, pos);
      };
      const up = () => {
        done();
        host.classList.remove('is-drag');
        host.removeEventListener('pointermove', mv);
        host.removeEventListener('pointerup', up);
        host.removeEventListener('pointercancel', up);
      };
      host.addEventListener('pointermove', mv);
      host.addEventListener('pointerup', up);
      host.addEventListener('pointercancel', up);
    };
    const onWheel = (e) => {
      if (isPano) return;
      e.preventDefault();
      wheelAt(si, ci, e.deltaY);
    };
    host.addEventListener('pointerdown', onPointerDown);
    host.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      host.removeEventListener('pointerdown', onPointerDown);
      host.removeEventListener('wheel', onWheel);
    };
  }, [stageEl, editor, si, ci, isPano]);

  /* The live paint. The newest photo, look and crop are read through dataRef, so
     the deps can be the plain strings that say when those changed: the tick this
     sets must never feed back into them, or every frame would schedule another. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: sig/cellSig/pv are the change signal for dataRef
  useEffect(() => {
    if (!liveEl) return undefined;
    const raf = requestAnimationFrame(() => {
      const d = dataRef.current;
      const ok = d.im && d.look
        ? paintLive(liveEl, d.im, d.look, d.cell, d.rc, d.fit, before, bump)
        : false;
      setOnLive((was) => (was === ok ? was : ok));
      setTick((t) => t + 1);
    });
    return () => cancelAnimationFrame(raf);
  }, [liveEl, sig, cellSig, before, pv, nudge]);

  if (!editor || !sl || !im || !look || !cell || !rc) return null;

  const adj = look.adj;
  const setAdj = (k, v) => setPhotoLook(im.id, { adj: { [k]: Math.round(v) } });
  const groupOff = (keys) => keys.every((k) => !adj[k]);
  const resetGroup = (keys) => {
    const patch = {};
    keys.forEach((k) => { patch[k] = 0; });
    setPhotoLook(im.id, { adj: patch });
  };
  const toggle = (k) => setOpen((o) => {
    openMemory = { ...o, [k]: !o[k] };
    return openMemory;
  });
  const zoom = cell.zoom || 1;
  const url = getPreview(im.id, look) || (look.rot ? '' : im.thumb);
  const exact = hasPreview(im.id, look);
  const showLive = onLive && (dragging || before || !exact);
  const scopeOn = !scope || scope.on !== false;
  const scopeKind = normalizeKind(scope && scope.kind);
  const others = slides.length - 1;

  return (
    <Dialog
      open
      onClose={closeEditor}
      fullScreen={isMobile}
      className="cc-modal"
      aria-labelledby="cc-edit-title"
      slotProps={{
        paper: { className: 'cc-root export-sheet cc-ed' + (isMobile ? ' is-phone' : '') },
        backdrop: { style: { background: 'rgba(12,12,14,.66)' } },
      }}
    >
      <div className="sheet-head">
        <h3 id="cc-edit-title">Frame, turn, grade</h3>
        <span className="hint">
          {'Slide ' + pad2(si + 1) + ' · ' + cellRoleName(sl, ci) + ' · ' + im.name}
        </span>
        <button
          type="button"
          className="btn tiny ghost"
          aria-pressed={scopeOn}
          onClick={() => setScope({ on: !scopeOn })}
        >
          Scope
        </button>
        {/* On a phone the corner letters are 12x11px at 40% opacity over the photo,
            and three 40px targets do not fit across a 108px box — so the kind
            switch lives out here instead, sized like the rest of the controls.
            ScopeBox hides its own switch at .is-phone; this is the only way in. */}
        {isMobile && scopeOn && (
          <div className="seg cc-ed-scopekind">
            {SCOPE_KINDS.map((k) => (
              <button
                key={k.key}
                type="button"
                aria-pressed={k.key === scopeKind}
                aria-label={k.label}
                title={k.label}
                onClick={() => setScope({ kind: k.key })}
              >
                {k.short}
              </button>
            ))}
          </div>
        )}
        <button type="button" className="btn tiny" onClick={closeEditor}>Done</button>
      </div>

      <div className="cc-ed-body" onPointerDown={() => setDragging(true)}>
        <div className="cc-ed-view">
          <div className={'cc-ed-row' + (refI == null ? '' : ' has-ref')} ref={setRowEl}>
            {refI == null ? null : (
              <div
                className="cc-ed-ref"
                style={{ width: refW + 'px', height: stageH ? stageH + 'px' : undefined }}
                aria-label={'Slide ' + pad2(refI + 1) + ', for comparison'}
              >
                <RefSlide i={refI} slides={slides} panos={panos} design={design} imgById={imgById} previews={pv} />
                <span className="cc-ed-reftag mono">{pad2(refI + 1)}</span>
              </div>
            )}
            <div
              className={'cc-ed-stage' + (fit === 'contain' ? ' is-contain' : '')}
              ref={setStageEl}
              style={{
                aspectRatio: rc.w + ' / ' + rc.h,
                height: stageH ? stageH + 'px' : undefined,
                background: design.bg,
              }}
            >
              {url ? <div className="cc-ed-img" style={cellLayerStyle(im, cell, rc, isPano ? null : stored, url)} /> : null}
              <canvas ref={setLiveEl} className={'cc-ed-live' + (showLive ? ' is-on' : '')} />
              {before ? <span className="cc-ed-badge mono">Before</span> : null}
              <ScopeBox source={onLive ? liveEl : null} watch={tick} live={dragging} mobile={isMobile} />
            </div>
          </div>

          <div className="cc-ed-under" style={underW ? { maxWidth: underW + 'px' } : undefined}>
            {others > 0 ? (
            // biome-ignore lint/a11y/useSemanticElements: a fieldset's legend has nowhere to sit in a scrolling strip
            <div className="cc-ed-picks" ref={picksRef} role="group" aria-label="Slide to match against">
              {slides.map((s, i) => {
                const now = i === si;
                return (
                  <button
                    key={s.id || i}
                    type="button"
                    className={'cc-ed-pick' + (now ? ' is-now' : '') + (i === refI ? ' is-ref' : '')}
                    aria-pressed={i === refI}
                    disabled={now}
                    title={now ? 'This slide' : 'Match against slide ' + pad2(i + 1)}
                    onClick={() => setRefI(i === refI ? null : i)}
                  >
                    <RefSlide i={i} slides={slides} panos={panos} design={design} imgById={imgById} previews={pv} />
                    <span className="cc-ed-picktag mono">{now ? '●' : pad2(i + 1)}</span>
                  </button>
                );
              })}
            </div>
            ) : null}

            <span className="hint cc-ed-tip">
              {isPano
                ? 'Drag any part to move the whole panorama'
                : isMobile ? 'Drag to reframe' : 'Drag to reframe · scroll to zoom'}
              {' · hold to see the before'}
              {others > 0 ? ' · tap a slide to match against' : ''}
            </span>
          </div>
        </div>

        <div className="cc-ed-panel">
          <Group
            id="framing"
            title="Framing"
            open={open.framing}
            onToggle={() => toggle('framing')}
            reset={isPano ? null : { label: 'Reset crop', run: () => resetCropAt(si, ci) }}
          >
            {isPano ? (
              <p className="hint">This photo is spread across a panorama — drag any part to move the whole run.</p>
            ) : (
              <>
                <div className="cc-ed-row2">
                  <span className="lbl">Sits</span>
                  <div className="seg">
                    <button type="button" aria-pressed={fit === 'fill'} onClick={() => setCellFit(si, ci, 'fill')}>Fill</button>
                    <button type="button" aria-pressed={fit === 'contain'} onClick={() => setCellFit(si, ci, 'contain')}>Fit</button>
                  </div>
                  <span className="hint">{fit === 'fill' ? 'cropped to the cell' : 'whole photo, on the fill colour'}</span>
                </div>
                <SliderRow
                  label="Zoom"
                  value={zoom}
                  min={1}
                  max={zoomSliderMax(zoom)}
                  step={0.01}
                  def={1}
                  suffix={Math.round(zoom * 100) + '%'}
                  ends={['whole frame', 'closer']}
                  onChange={(v) => setZoomAt(si, ci, v)}
                />
                <div className="cc-ed-row2">
                  <span className="lbl">Position</span>
                  <div className="cc-ed-nudge">
                    <button type="button" className="btn tiny ghost" aria-label="Nudge up" onClick={() => nudgeCell(si, ci, 0, -1)}>↑</button>
                    <button type="button" className="btn tiny ghost" aria-label="Nudge left" onClick={() => nudgeCell(si, ci, -1, 0)}>←</button>
                    <button type="button" className="btn tiny ghost" aria-label="Centre" onClick={() => centerCell(si, ci)}>◎</button>
                    <button type="button" className="btn tiny ghost" aria-label="Nudge right" onClick={() => nudgeCell(si, ci, 1, 0)}>→</button>
                    <button type="button" className="btn tiny ghost" aria-label="Nudge down" onClick={() => nudgeCell(si, ci, 0, 1)}>↓</button>
                  </div>
                </div>
              </>
            )}
          </Group>

          <Group
            id="rotation"
            title="Rotation"
            open={open.rotation}
            onToggle={() => toggle('rotation')}
            reset={{ label: 'Straight', off: !look.rot, run: () => setPhotoLook(im.id, { rot: 0 }) }}
          >
            <SliderRow
              label="Angle"
              value={look.rot}
              min={-180}
              max={180}
              step={0.5}
              def={0}
              ends={['−180°', '180°']}
              onChange={(v) => setPhotoLook(im.id, { rot: v })}
            />
            <div className="cc-ed-row2">
              <span className="lbl">Turn</span>
              <button type="button" className="btn tiny ghost" onClick={() => setPhotoLook(im.id, { rot: look.rot - 90 })}>⟲ 90°</button>
              <button type="button" className="btn tiny ghost" onClick={() => setPhotoLook(im.id, { rot: look.rot + 90 })}>⟳ 90°</button>
            </div>
            <div className="cc-ed-row2">
              <span className="lbl">Corners</span>
              <div className="seg">
                <button
                  type="button"
                  aria-pressed={look.corner === 'zoom'}
                  disabled={!look.rot || isQuarterTurn(look.rot)}
                  onClick={() => setPhotoLook(im.id, { corner: 'zoom' })}
                >
                  Crop in
                </button>
                <button
                  type="button"
                  aria-pressed={look.corner === 'fill'}
                  disabled={!look.rot || isQuarterTurn(look.rot)}
                  onClick={() => setPhotoLook(im.id, { corner: 'fill' })}
                >
                  Show fill
                </button>
              </div>
              <span className="hint">
                {!look.rot ? 'turn the photo first'
                  : isQuarterTurn(look.rot) ? 'a quarter turn has no empty corners'
                    : look.corner === 'zoom' ? 'crops so no empty corner shows'
                      : 'corners fall through to the fill colour'}
              </span>
            </div>
          </Group>

          {ADJ_GROUPS.map((grp) => (
            <Group
              key={grp.key}
              id={grp.key}
              title={grp.label}
              open={open[grp.key]}
              onToggle={() => toggle(grp.key)}
              reset={{ off: groupOff(grp.keys), run: () => resetGroup(grp.keys) }}
            >
              {grp.keys.map((k) => (
                <SliderRow
                  key={k}
                  label={ADJ_LABELS[k]}
                  value={adj[k]}
                  min={-100}
                  max={100}
                  step={1}
                  def={0}
                  ends={ADJ_ENDS[k]}
                  onChange={(v) => setAdj(k, v)}
                />
              ))}
            </Group>
          ))}

          <div className="cc-ed-foot">
            <p className="hint">The turn and the grade follow this photo into every slide that uses it.</p>
            {!isPano && (
              <label className="cc-ed-check">
                <input
                  type="checkbox"
                  checked={!!overridden}
                  onChange={(e) => setCellLookMode(si, ci, e.target.checked ? 'none' : 'photo')}
                />
                <span>Use the original on this slide</span>
              </label>
            )}
            <div className="cc-ed-row2">
              <button
                type="button"
                className="btn tiny ghost"
                disabled={isNeutralAdj(adj)}
                onClick={() => setPhotoLook(im.id, { adj: NEUTRAL_ADJ })}
              >
                Reset the grade
              </button>
              <button type="button" className="btn tiny ghost" onClick={() => resetPhotoLook(im.id)}>
                Reset everything
              </button>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
